import React, { useState } from 'react';
import axios from 'axios';
import { Download, Loader2, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact per-designer 6M / 12M download dropdown.
 * Offers PDF (rich visual) and Excel (raw data) formats.
 */
export const DesignerReportDownload = ({
  designerId,
  designerName,
  size = 'icon',
  variant = 'ghost',
  testIdPrefix = 'download-report',
}) => {
  const [downloading, setDownloading] = useState(null); // '6-pdf' | '12-pdf' | '6-xlsx' | '12-xlsx' | null

  const handleDownload = async (months, format, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!designerId) return;
    const key = `${months}-${format}`;
    setDownloading(key);
    try {
      const token = localStorage.getItem('arrise_token');
      const now = new Date();
      const end = new Date(now.getFullYear(), now.getMonth(), 1);
      const start = new Date(end.getFullYear(), end.getMonth() - (months - 1), 1);

      const endpoint = format === 'pdf'
        ? `${BACKEND_URL}/api/reports/member-performance/pdf`
        : `${BACKEND_URL}/api/reports/member-performance/excel`;

      const response = await axios.get(endpoint, {
        params: {
          designer_id: designerId,
          start_year: start.getFullYear(),
          start_month: start.getMonth() + 1,
          end_year: end.getFullYear(),
          end_month: end.getMonth() + 1,
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      link.setAttribute(
        'download',
        `${(designerName || 'Member').replace(/\s+/g, '_')}_${months}M_Performance.${ext}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Report download failed:', error);
      const msg =
        error?.response?.status === 404
          ? 'No scores found for this period'
          : 'Failed to download report';
      alert(msg);
    } finally {
      setDownloading(null);
    }
  };

  const busy = downloading !== null;

  const DLItem = ({ months, format, label, icon: Icon, iconColor }) => {
    const key = `${months}-${format}`;
    const isBusy = downloading === key;
    return (
      <DropdownMenuItem
        onClick={(e) => handleDownload(months, format, e)}
        disabled={isBusy}
        data-testid={`${testIdPrefix}-${months}m-${format}-${designerId}`}
        className="cursor-pointer"
      >
        {isBusy ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Icon className={`w-4 h-4 mr-2 ${iconColor}`} />
        )}
        {label}
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size={size}
          variant={variant}
          className="h-8 w-8 p-0"
          disabled={busy}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testIdPrefix}-${designerId}`}
          aria-label={`Download report for ${designerName}`}
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4 text-emerald-600" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuLabel className="text-xs text-slate-500">
          Visual Report (PDF)
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DLItem months={6} format="pdf" label="6-Month Report (PDF)" icon={FileText} iconColor="text-rose-600" />
        <DLItem months={12} format="pdf" label="12-Month Report (PDF)" icon={FileText} iconColor="text-rose-600" />
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-slate-500">
          Raw Data (Excel)
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DLItem months={6} format="xlsx" label="6-Month Data (Excel)" icon={FileSpreadsheet} iconColor="text-emerald-600" />
        <DLItem months={12} format="xlsx" label="12-Month Data (Excel)" icon={FileSpreadsheet} iconColor="text-emerald-600" />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DesignerReportDownload;
